import produce from 'immer'
import { assign, filter, forEach, get, isEmpty, map, set, uniqBy } from 'lodash'

import { METADATA_TYPES } from '../../../common/constants'
import { buildTreeHierarchy, transformTree } from '../../../common/helpers'

import {
  ADD_INGREDIENT,
  DELETE_INGREDIENT,
  EXPAND_INGREDIENT_MAIN_TREE_KEYS,
  EXPAND_INGREDIENT_SUPPORTING_TREE_KEYS,
  FETCH_INGREDIENTS,
  MOVE_INGREDIENTS,
  RENAME_INGREDIENT,
  REPLACE_INGREDIENT,
  RESET_INGREDIENT_TREE,
  REVERT_REPLACE_INGREDIENT,
  SAVE_INGREDIENTS,
  SET_DATA_FOR_RENAMING_INGREDIENT,
  SET_IS_CURATION_ENABLED_INGREDIENT,
} from '../action-types'
import { INGREDIENTS_INITIAL_STATE } from '../initial-states'

const ingredientsReducer = (state = INGREDIENTS_INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_INGREDIENTS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_INGREDIENTS.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } =
          action.payload.data.ingredients ||
          action.payload.data.ingredientsByKeyword

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.INGREDIENTS)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          latestEvent,
        })
      })

    case FETCH_INGREDIENTS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_INGREDIENTS.CLEANUP:
      return { ...INGREDIENTS_INITIAL_STATE }

    case SAVE_INGREDIENTS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          latestEvent: undefined,
        }),
      )

    case SAVE_INGREDIENTS.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } = action.payload.data.saveIngredients

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.INGREDIENTS)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
          latestEvent,
        })
      })

    case SAVE_INGREDIENTS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
          latestEvent: undefined,
        }),
      )

    case DELETE_INGREDIENT.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case DELETE_INGREDIENT.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.deleteIngredient

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.INGREDIENTS)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case DELETE_INGREDIENT.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case REPLACE_INGREDIENT.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case REPLACE_INGREDIENT.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.replaceIngredient

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.INGREDIENTS)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case REPLACE_INGREDIENT.FAIL:
      return produce(state, nextState => {
        const { graphQLErrors, message } = action.error

        const conflicts = get(
          graphQLErrors,
          '[0].extensions.exception.conflicts',
        )

        return assign(nextState, {
          isLoading: false,
          error: { message, conflicts },
        })
      })

    case REVERT_REPLACE_INGREDIENT.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case REVERT_REPLACE_INGREDIENT.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.revertReplaceIngredient

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.INGREDIENTS)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case REVERT_REPLACE_INGREDIENT.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case SET_IS_CURATION_ENABLED_INGREDIENT.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case SET_IS_CURATION_ENABLED_INGREDIENT.COMPLETE:
      return produce(state, nextState => {
        const updatedIngredient =
          action.payload.data.setIsCurationEnabledIngredient

        const treeData = transformTree(nextState.initialData, node =>
          node.id === updatedIngredient.id
            ? {
                ...node,
                isCurationEnabled: updatedIngredient.isCurationEnabled,
              }
            : node,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case SET_IS_CURATION_ENABLED_INGREDIENT.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case ADD_INGREDIENT:
      return produce(state, nextState => {
        const { parentPath } = action.payload
        const isRootNodeAdded = isEmpty(parentPath)

        const arrayToAddNodeTo = isRootNodeAdded
          ? nextState.data
          : get(nextState.data, `${parentPath}.children`, [])

        arrayToAddNodeTo.push(action.payload)

        const treeDataWithNewNode = isRootNodeAdded
          ? arrayToAddNodeTo
          : set(nextState.data, `${parentPath}.children`, arrayToAddNodeTo)

        return assign(nextState, {
          ...nextState,
          data: treeDataWithNewNode,
          isDirty: true,
          dataForRenaming: { id: action.payload.id },
        })
      })

    case RENAME_INGREDIENT:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          data: set(
            nextState.data,
            `${action.payload.path}.name`,
            action.payload.name,
          ),
          isDirty: true,
        }),
      )

    case SET_DATA_FOR_RENAMING_INGREDIENT:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          dataForRenaming: action.payload,
        }),
      )

    case MOVE_INGREDIENTS:
      return produce(state, nextState => {
        const { nodesToMove, destinationNode } = action.payload
        let newTreeData = nextState.data

        // remove nodes to be moved
        forEach(nodesToMove, ({ id: idToMove, parentPath }) => {
          const isMovingRootNode = isEmpty(parentPath)

          if (isMovingRootNode) {
            newTreeData = filter(newTreeData, ({ id }) => id !== idToMove)
          } else {
            newTreeData = set(
              newTreeData,
              `${parentPath}.children`,
              filter(
                get(newTreeData, `${parentPath}.children`),
                ({ id }) => id !== idToMove,
              ),
            )
          }
        })

        const isMovedToRoot = destinationNode.level === 1

        if (isMovedToRoot) {
          newTreeData = [
            ...newTreeData,
            ...map(nodesToMove, node => ({
              ...node,
              parentId: null,
              level: 1,
              idPath: [node.id],
              namePath: [node.name],
            })),
          ]
        } else {
          newTreeData = transformTree(newTreeData, node =>
            node.id === destinationNode.id
              ? {
                  ...node,
                  children: uniqBy(
                    [
                      ...(node.children || []),
                      ...map(nodesToMove, node => ({
                        ...node,
                        parentId: destinationNode.id,
                        level: destinationNode.level - 1,
                        idPath: [...destinationNode.idPath, node.id],
                        namePath: [...destinationNode.namePath, node.name],
                      })),
                    ],
                    'id',
                  ),
                }
              : node,
          )
        }

        return assign(nextState, {
          ...nextState,
          isDirty: true,
          data: buildTreeHierarchy(newTreeData, METADATA_TYPES.INGREDIENTS),
        })
      })

    case EXPAND_INGREDIENT_MAIN_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          mainTreeExpandedKeys: action.payload,
        }),
      )

    case EXPAND_INGREDIENT_SUPPORTING_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          supportingTreeExpandedKeys: action.payload,
        }),
      )

    case RESET_INGREDIENT_TREE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          data: nextState.initialData,
          isDirty: false,
          dataForRenaming: undefined,
        }),
      )

    default:
      return state
  }
}

export default ingredientsReducer
